import { CaretDownOutlined } from '@ant-design/icons';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import EditOutlined from '@ant-design/icons/EditOutlined';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import { useGetAccounts } from '@vindr/services/actions/account.action';
import { useGetKeyImages } from '@vindr/services/actions/attachment.action';
import { useGetLayouts } from '@vindr/services/actions/layout.action';
import { useGetOrderDetail, useGetOrders } from '@vindr/services/actions/order.action';
import {
  useCreateReport,
  useGetReports,
  useUpdateApproveReport,
  useUpdateCancelReport,
  useUpdateEditReport,
  useUpdateSubmitReport,
} from '@vindr/services/actions/report.action';
import { useGetDetailStudy } from '@vindr/services/actions/study.action';
import { useGetTemplates } from '@vindr/services/actions/template.action';
import { AGENCY } from '@vindr/services/constants/common.constant';
import { SORT_ORDER, USER_STATUS } from '@vindr/services/constants/filter.constant';
import { MESSAGE } from '@vindr/services/constants/message.constant';
import { ORDER_STATUS } from '@vindr/services/constants/order.constant';
import { PERMISSIONS } from '@vindr/services/constants/permissions.constant';
import { LAYOUT_TYPE, REPORT_STATUS } from '@vindr/services/constants/report.constant';
import {
  ALLTIME_STUDYDATE,
  MATCH_STATUS,
  MODALITIES,
} from '@vindr/services/constants/study.constant';
import keycloak from '@vindr/services/keycloak';
import { conditionObj, getArrayVal, getDateFormatted } from '@vindr/utils/helpers/common.helper';
import useWindowSize from '@vindr/utils/hooks/useWindowSize';
import {
  Empty as AntEmpty,
  Button,
  Col,
  ConfigProvider,
  Form,
  Row,
  Select,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { get, isEmpty, isEqual } from 'lodash';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Updater } from 'use-immer';
import Empty from '../../ui/Empty';
import Loading from '../../ui/Loading';
import TextEditor from '../../ui/TextEditor';
import { CaptureHandle } from '../Capture-v2/Webcam/context/type';
import WebcamRecorder from '../Capture-v2/WebcamRecorder';
import MatchOrder from '../MatchOrder';
import CreateOrderModal from '../Order/CreateOrder/CreateOrderModal';
import OpenWebcamButton from './OpenWebcamButton';
import PanelInfo from './PanelInfo';
import PatientInformation from './PatientInformation';
import PrintModal from './Print';
import ReportHistory from './ReportHistory';
import SelectReport from './SelectReport';
import SelectTemplate from './SelectTemplate';
import Report from './index';

const { Title } = Typography;

const ReportForm = ({
  study_id = null,
  order_id = null,
  assign = null,
  attachmentParams,
  onMissingOrder = null,
  recordVideo,
  setRecordVideo,
  warningCloseCamera,
  message,
  iframe,
  modal,
}: Omit<ReportFormProps, 'onCancel' | 'visible' | 'motion'>) => {
  const { t } = useTranslation('Vindoc');
  const [iframeState] = iframe || [];
  const userInfo = keycloak.getUserInfo();
  const [openPrintDialog, setOpenPrintDialog] = useState<boolean>(false);
  const [openSelectTemplate, setOpenSelectTemplate] = useState<boolean>(false);
  const [openMatchOrder, setOpenMatchOrder] = useState<boolean>(false);
  const [openCreateOrder, setOpenCreateOrder] = useState<boolean>(false);
  const [pendingOrderIds, setPendingOrderIds] = useState<string[]>([]);
  const openRecordVideo = recordVideo.open;
  const [isUploading, setIsUploading] = useState(false);

  const { data: responseStudy } = useGetDetailStudy(
    { study_id, ...ALLTIME_STUDYDATE },
    { enabled: !!study_id }
  );
  const study = responseStudy?.data?.[0];

  const { data: responseOrder } = useGetOrderDetail(order_id, { enabled: !!order_id });
  const order = responseOrder?.data;

  const [reportForm] = Form.useForm();
  const layoutsQuery = useGetLayouts({ offset: 0, limit: 100, active: true })?.data?.data || [];
  const layouts = layoutsQuery.filter(
    (it) =>
      (it?.institution_id === study?.institution_id ||
        it?.institution_id === assign?.order?.institution_id ||
        it?.institution_id === order?.institution_id) &&
      (it?.status === LAYOUT_TYPE.PUBLIC || it?.user_id === userInfo?.id)
  );
  const {
    data: responseMatchedOrder,
    isInitialLoading: isLoadingResponseMatchedOrder,
    isRefetching: isRefetchingResponseMatchedOrder,
  } = useGetOrders(
    { offset: 0, limit: 100, study_id: study?.study_id },
    { enabled: !!study?.study_id }
  );

  // const matchedOrders = (
  //   study ? responseMatchedOrder?.data : order ? [order] : [assign?.order]
  // ) satisfies ReportFormStates['matchedOrders'];

  const matchedOrders = useMemo(() => {
    if (study) {
      return responseMatchedOrder?.data;
    } else if (order) {
      return [order];
    } else if (assign?.order) {
      return [assign?.order];
    }

    return [];
  }, [
    assign?.order,
    order,
    responseMatchedOrder?.data,
    study,
  ]) satisfies ReportFormStates['matchedOrders'];

  const [selectedOrder, setSelectedOrder] = useState<ReportFormStates['SelectedOrderType']>(null);
  const {
    data: reportsQuery,
    isInitialLoading: isLoadingReportsQuery,
    isRefetching: isRefetchingReportsQuery,
  } = useGetReports(
    {
      offset: 0,
      limit: 100,
      order_id: selectedOrder?.id,
      institution_ids: [selectedOrder?.institution_id],
    },
    {
      enabled: !!selectedOrder?.institution_id && !!selectedOrder?.id,
    }
  );
  // const reports = selectedOrder ? reportsQuery : [];

  const reports = useMemo(() => {
    return selectedOrder ? reportsQuery?.data : [];
  }, [selectedOrder, reportsQuery?.data]);

  const [selectedReport, setSelectedReport] = useState<ReportFormStates['selectedReport']>(null);

  const displayedLayouts =
    selectedReport?.status === REPORT_STATUS.READING ? layouts : layoutsQuery;

  const sessionUser = useGetAccounts(
    {
      offset: 0,
      limit: 100,
      statuses: [USER_STATUS.ACTIVE],
      ids: [selectedReport?.session],
    },
    {
      enabled: !!selectedReport?.session,
    }
  )?.data?.data?.[0];
  const templates = useGetTemplates({ offset: 0, limit: 100 })?.data?.data || [];
  const [newReport, setNewReport] = useState(null);
  const { mutate: createReport } = useCreateReport();
  const { mutate: saveReport } = useUpdateSubmitReport();
  const { mutate: approveReport } = useUpdateApproveReport();
  const { mutate: editReport } = useUpdateEditReport();
  const { mutate: cancelReport } = useUpdateCancelReport();

  const {
    data: reportList,
    isInitialLoading: isLoadingReportList,
    isRefetching: isRefetchingReportList,
  } = useGetReports(
    {
      limit: 100,
      offset: 0,
      order_id: selectedOrder?.id,
      sort_field: 'updated_at',
      sort_order: SORT_ORDER['DESC'],
    },
    { enabled: !!selectedOrder?.id }
  );

  // const reportsHistory = reportList?.data || [];
  const reportsHistory = useMemo(() => reportList?.data || [], [reportList?.data]);
  const webcamRef = useRef<CaptureHandle>(null);

  const keyImages =
    useGetKeyImages(study?.study_instance_uid || assign?.order?.study?.study_instance_uid, {
      enabled: !!(
        (study?.study_instance_uid || assign?.order?.study?.study_instance_uid) &&
        keycloak.hasOneOfPerm([PERMISSIONS.report.keyimage])
      ),
    })?.data || [];

  useEffect(() => {
    if (isLoadingReportsQuery || isRefetchingReportsQuery) return;

    if (assign && reports.every((it) => it.status === REPORT_STATUS.APPROVED)) {
      //? We should select a report submitted by me
      const filterReport = reports.filter((it) => it?.submitter_id === userInfo?.id);
      const report = filterReport
        ? filterReport.reduce((a, b) => (a.updated_at > b.updated_at ? a : b), filterReport[0])
        : null;

      setSelectedReport(report);
    } else {
      setSelectedReport(
        reports ? reports.reduce((a, b) => (a.updated_at > b.updated_at ? a : b), reports[0]) : null
      );
    }
  }, [reports, isLoadingReportsQuery, isRefetchingReportsQuery]);

  useEffect(() => {
    //? Make sure api get matchedOrders done before call missing order
    if (matchedOrders !== undefined && matchedOrders != null && matchedOrders.length === 0) {
      onMissingOrder?.();
    }
    if (!selectedOrder) {
      const findOrder = order && matchedOrders && matchedOrders.find((it) => it.id === order.id);

      setSelectedOrder(findOrder || matchedOrders?.[0]);

      return;
    }
    const findOrder = matchedOrders && matchedOrders.find((it) => it.id === selectedOrder.id);

    setSelectedOrder(findOrder || matchedOrders?.[0]);
  }, [matchedOrders, order]);

  useEffect(() => {
    reportForm.resetFields();
    if (newReport) {
      reportForm.setFieldsValue(newReport);
      // if (!reportForm.getFieldValue('technician_id')) {
      //   reportForm.setFieldValue('technician_id', userInfo?.id);
      // }
    } else {
      reportForm.setFieldsValue(selectedReport);
    }
  }, [selectedReport, newReport, userInfo?.id]);

  const handleCreateReport = (oldData = null) => {
    if (!selectedOrder) {
      message.error(t('No selected order'));

      return;
    }
    createReport(
      {
        institution_id: selectedOrder?.institution_id,
        order_id: selectedOrder?.id,
        order_assign_id: assign?.id,
      },
      {
        onSuccess: (res) => {
          console.log('CREATE REPORT', res);
          if (oldData) setNewReport(oldData);
        },
        onError: (error) => {
          message.error(t('Create error'));
        },
      }
    );
  };

  const handleEditReport = () => {
    if (selectedReport?.status == REPORT_STATUS.APPROVED) {
      handleCreateReport(selectedReport);
    } else {
      editReport(
        { id: selectedReport?.id, data: { order_assign_id: assign?.id } },
        {
          onSuccess: (res) => {
            console.log('EDIT REPORT', res);
          },
          onError: (error) => {
            message.error(t('Create error'));
          },
        }
      );
    }
  };

  const handleCancelReport = () => {
    cancelReport(
      { id: selectedReport.id, data: { order_assign_id: assign?.id } },
      {
        onSuccess: (res) => {
          setNewReport(null);
          console.log('CANCEL EDIT REPORT', res);
        },
        onError: (error) => {
          message.error(t('Create error'));
        },
      }
    );
  };

  const handleSave = useCallback(() => {
    function onSave() {
      reportForm
        .validateFields()
        .then((values) => {
          saveReport(
            {
              id: selectedReport.id,
              data: {
                ...values,
                order_assign_id: assign?.id,
              },
            },
            {
              onSuccess: (res) => {
                setNewReport(null);
                console.log('SAVE REPORT', res);
              },
              onError: () => {
                message.error(t('Create error'));
              },
            }
          );
        })
        .catch((err) => {
          console.error('Save error', err);
        });
    }

    if (recordVideo.open) {
      return warningCloseCamera(
        async () => {
          webcamRef.current.stopRecord(true);
          await webcamRef?.current?.handleUploadFiles();
          onSave();
        },
        {
          title: t('You are recording a video. Do you want to stop recording and upload the data?'),
          okText: t('Save'),
          cancelText: t('Cancel'),
        }
      );
    }

    onSave();
  }, [
    assign?.id,
    message,
    recordVideo,
    reportForm,
    saveReport,
    selectedReport?.id,
    t,
    warningCloseCamera,
  ]);

  const handleApprove = () => {
    reportForm
      .validateFields()
      .then((values) => {
        approveReport(
          {
            id: selectedReport.id,
            data: {
              content: values.content,
              conclusion: values.conclusion,
              technician_id: values.technician_id,
              order_assign_id: assign?.id,
              layout_id: values.layout_id,
            },
          },
          {
            onSuccess: (res) => {
              setNewReport(null);
              console.log('APPROVE REPORT', res);
            },
            onError: (error) => {
              message.error(t('Create error'));
            },
          }
        );
      })
      .catch((err) => {
        console.error('Approve error', err);
      });
  };

  const canSeeReport = useCallback(
    (report) => {
      //? Report is editing by someone
      if (report?.status === REPORT_STATUS.READING && report?.session !== userInfo?.id)
        return false;
      //? open from study can view all
      //? admin can view all
      if (study || keycloak.hasOneOfPerm([PERMISSIONS.assigned.admin_assignedlist])) return true;
      //? Report is in my processing
      if (report?.session === userInfo?.id) return true;
      //? Report submit by me
      return report?.submitter_id === userInfo?.id;
    },
    [study, userInfo?.id]
  );

  const breakpoint = iframeState?.open ? 1800 : 1300;
  const windowSize = useWindowSize();

  const paramsUploadRecorder = useMemo(() => {
    let headerParams = attachmentParams;

    if (study) {
      headerParams = {
        accession_number: getArrayVal(study.order_group),
        device: study.device_id,
        institution_id: study.institution_id,
        modality: getArrayVal(study.modality) || MODALITIES[13],
        patient_id: study.patient_id,
        ...conditionObj('patient_name', study.patient_name),
        study_datetime: study.study_date,
        ...conditionObj('study_description', getArrayVal(study.study_desc)),
        study_instance_uid: study.study_instance_uid,
      };
    } else if (assign) {
      headerParams = {
        accession_number: assign?.order?.order_group,
        device: assign?.order?.device_id,
        institution_id: assign?.order?.institution_id,
        modality: assign?.order?.procedure?.modality || MODALITIES[13],
        patient_id: assign?.order?.visited_patient?.patient_id,
        ...conditionObj('patient_name', assign?.order?.visited_patient?.name),
        study_datetime: assign?.order?.study?.study_date || assign?.order?.scheduled_date,
        ...conditionObj('study_instance_uid', assign?.order?.study?.study_instance_uid),
      };
    }

    return headerParams;
  }, [study, attachmentParams, assign]);

  return (
    <div
      className={'flex w-full flex-col space-y-3 overflow-x-visible'}
      style={{ height: windowSize.width >= breakpoint ? 'calc(100dvh - 112px)' : 'auto' }}
    >
      <ConfigProvider
        theme={{
          token: {
            screenXXLMin: breakpoint,
            screenXXL: breakpoint,
          },
        }}
      >
        <div className="flex flex-wrap items-center justify-between">
          <Title level={5}>
            {selectedOrder?.procedure?.compact_name || selectedOrder?.procedure?.name
              ? `${
                  selectedOrder?.procedure?.compact_name || selectedOrder?.procedure?.name || ''
                } ${
                  (selectedOrder?.scheduled_date &&
                    dayjs(selectedOrder?.scheduled_date).format(getDateFormatted())) ||
                  ''
                } (${ORDER_STATUS[selectedOrder?.status] || ''} ${dayjs(
                  selectedOrder?.updated_at
                ).format(getDateFormatted() + ' HH:mm:ss')})`
              : ''}
          </Title>
          <div className="flex space-x-2 overflow-auto">
            {
              //? Can create report when: have selected oder and all report is approved
              selectedOrder &&
                reportsHistory.every((it) => it.status === REPORT_STATUS.APPROVED) && (
                  <Button
                    size="small"
                    className="flex items-center"
                    onClick={handleCreateReport}
                    disabled={!keycloak.hasOneOfPerm([PERMISSIONS.report.create])}
                  >
                    <PlusOutlined />
                    {t('New Report')}
                  </Button>
                )
            }
            {
              //? Can edit report when: have selected report, that report is not reading and all report is [approved, submitted]
              canSeeReport(selectedReport) &&
              selectedReport &&
              selectedReport?.status != REPORT_STATUS.READING &&
              reportsHistory.every(
                (it) =>
                  it.status === REPORT_STATUS.APPROVED || it.status === REPORT_STATUS.SUBMITTED
              ) ? (
                <Button
                  size="small"
                  className="flex items-center"
                  onClick={handleEditReport}
                  disabled={!keycloak.hasOneOfPerm([PERMISSIONS.report.create])}
                >
                  <EditOutlined />
                  {t('Edit Report')}
                </Button>
              ) : null
            }
            {
              //? Show cancel button when: have selected report, that report is reading and session is me
              selectedReport &&
              selectedReport?.status == REPORT_STATUS.READING &&
              selectedReport?.session == userInfo.id ? (
                <Button size="small" className="flex items-center" onClick={handleCancelReport}>
                  <CloseOutlined />
                  {t('Cancel')}
                </Button>
              ) : null
            }
            <OpenWebcamButton
              recordVideo={recordVideo}
              setRecordVideo={setRecordVideo}
              warningCloseCamera={warningCloseCamera}
            />
          </div>
        </div>
        <Row gutter={[16, 24]} style={{ height: 'calc(100% - 73px)', overflow: 'hidden auto' }}>
          <Col
            sm={24}
            xxl={keyImages.length && !openRecordVideo ? 8 : openRecordVideo ? 0 : 10}
            // className="h-full overflow-scroll"
            style={{ paddingTop: 0, paddingBottom: 0 }}
          >
            <div className="flex h-full flex-col space-y-4">
              <PatientInformation
                name={study?.patient_name || order?.visited_patient?.name}
                id={study?.patient_id || order?.visited_patient?.patient_id}
                birthDate={study?.patient_birth_date || order?.visited_patient.birth_date}
                sex={study?.patient_sex || order?.visited_patient.sex}
              />
              <SelectReport
                shouldNotMatchOrCreateOrder={!!assign || !!order}
                orders={matchedOrders}
                selectedOrder={selectedOrder}
                onChangeOrder={(order) => setSelectedOrder(order)}
                onMatchOrder={() => setOpenMatchOrder(true)}
                onCreateOrder={() => setOpenCreateOrder(true)}
                study={study}
                loading={isLoadingResponseMatchedOrder || isRefetchingResponseMatchedOrder}
              />

              <ReportHistory
                reports={assign ? reportsHistory.filter((it) => canSeeReport(it)) : reportsHistory}
                loading={isLoadingReportList || isRefetchingReportList}
                {...{ selectedReport, setSelectedReport }}
              />
              <PanelInfo
                form={reportForm}
                report={canSeeReport(selectedReport) ? selectedReport : null}
                order={selectedOrder}
                study={study || assign?.order?.study}
              />
            </div>
          </Col>
          {keycloak.hasOneOfPerm([PERMISSIONS.report.keyimage]) &&
          keyImages.length &&
          !openRecordVideo ? (
            <Col sm={24} xxl={keyImages.length ? 3 : 0}>
              <Title level={4} className="mb-0 mt-2 pb-3 pt-0 min-[1200px]:mt-0">
                {t('Key Image')}
              </Title>
              <div
                className="flex w-full flex-wrap overflow-scroll min-[1200px]:flex-col min-[1200px]:flex-nowrap"
                // style={{ height: 'calc(100% - 30px)' }}
              >
                {keyImages.map(
                  (it, idx) =>
                    it?.imageSrc && (
                      <img
                        key={idx}
                        src={it?.imageSrc}
                        className="mb-1 mr-1 max-[1200px]:max-h-24"
                      />
                    )
                )}
              </div>
            </Col>
          ) : null}
          <Col
            sm={24}
            // xl={keyImages.length ? (openRecordVideo ? 8 : 14) : 12}
            xxl={openRecordVideo ? 16 : keyImages.length ? 13 : 14}
            className="w-full"
          >
            {
              /**
               * ? Not match order yet: show empty message: No order
               * ? Not select report yet: show empty message: No report
               * ? Can see report: show report form
               * ? Otherwise: Report is being edited by someone
               */

              isLoadingReportsQuery ||
              isRefetchingReportsQuery ||
              isLoadingResponseMatchedOrder ||
              isRefetchingResponseMatchedOrder ? (
                <div className="flex h-full items-center justify-center">
                  <Loading />
                </div>
              ) : !matchedOrders?.length ? (
                <Empty
                  className="m-0 flex h-full flex-col items-center justify-center text-lg"
                  image={AntEmpty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <div>
                      <div>{t('No order available')}</div>
                      <div>{t('You need to match study with order or create new order')}</div>
                    </div>
                  }
                />
              ) : !selectedReport ? (
                <Empty
                  image={AntEmpty.PRESENTED_IMAGE_SIMPLE}
                  description={t('No report available')}
                  className="m-0 flex h-full flex-col items-center justify-center text-lg"
                />
              ) : canSeeReport(selectedReport) ? (
                <div className="flex flex-col">
                  <Form
                    layout="vertical"
                    requiredMark
                    form={reportForm}
                    name="form"
                    className="form-filter-condition"
                    labelAlign="left"
                  >
                    <div className="mt-0 mb-1 flex w-full flex-wrap items-center justify-between">
                      <Title level={4} className="mb-0">
                        {t('Content')}
                      </Title>
                      <div className="flex flex-wrap items-start">
                        <Button
                          className="my-1 mx-1 min-w-[156px] flex-1"
                          size="small"
                          disabled={selectedReport?.status != REPORT_STATUS.READING}
                          onClick={() => setOpenSelectTemplate(true)}
                        >
                          {t('Choose Report Template')}
                        </Button>
                        <Form.Item
                          className="mx-1 my-0 flex-1"
                          name="layout_id"
                          rules={[{ required: true, message: t(MESSAGE.INLINE.IEM_0001) }]}
                        >
                          <Select
                            size="small"
                            placeholder={t('Select Layout')}
                            optionLabelProp="label"
                            suffixIcon={<CaretDownOutlined />}
                            disabled={selectedReport?.status != REPORT_STATUS.READING}
                            className="min-w-[156px] flex-1"
                          >
                            {displayedLayouts.map((it) => (
                              <Select.Option value={it.id} key={it.id} label={it.name}>
                                {it.name}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </div>
                    </div>
                    <Form.Item name="content" className="mb-3">
                      <TextEditor
                        disabled={selectedReport?.status !== REPORT_STATUS.READING}
                        menubar={false}
                        height={window.AGENCY === AGENCY.VINMEC ? 450 : 655}
                        onKeyDown={(e) => webcamRef.current?.handleKeyDown(e)}
                      />
                    </Form.Item>
                    {window.AGENCY === AGENCY.VINMEC && (
                      <Form.Item
                        name="conclusion"
                        className="mb-0"
                        label={
                          <Title level={4} className="mb-0">
                            {t('Conclusion')}
                          </Title>
                        }
                      >
                        <TextEditor
                          disabled={selectedReport?.status !== REPORT_STATUS.READING}
                          menubar={false}
                          height={217}
                          onKeyDown={(e) => webcamRef.current?.handleKeyDown(e as any)}
                        />
                      </Form.Item>
                    )}
                  </Form>
                </div>
              ) : (
                <Empty
                  image={AntEmpty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    isEmpty(sessionUser)
                      ? t('This report is being modified by another user')
                      : `${t('This report is being modified by')} ${sessionUser?.username}${
                          sessionUser?.fullname ? ` : ${sessionUser?.fullname}` : ''
                        }`
                  }
                />
              )
            }
          </Col>
          <Col
            lg={24}
            xxl={openRecordVideo ? 8 : 0}
            className="w-full min-[1200px]:h-full min-[1200px]:overflow-auto"
          >
            <WebcamRecorder
              study={study}
              assign={assign}
              params={paramsUploadRecorder}
              setRecording={setRecordVideo}
              ref={webcamRef}
              open={openRecordVideo}
              modal={modal}
              onUploading={setIsUploading}
            />
          </Col>
        </Row>

        <div className="footer flex justify-end">
          {
            //? Can save report when: have selected report, that report is reading and session is me
            canSeeReport(selectedReport) &&
            selectedReport?.status == REPORT_STATUS.READING &&
            selectedReport?.session == userInfo?.id ? (
              <Button type="primary" onClick={handleSave} className="ml-2" disabled={isUploading}>
                {t('Save')}
              </Button>
            ) : null
          }
          {
            //? Can approve report when: have selected report, that report is submitted
            canSeeReport(selectedReport) && selectedReport?.status == REPORT_STATUS.SUBMITTED ? (
              <Button
                type="primary"
                onClick={handleApprove}
                className="ml-2"
                disabled={!keycloak.hasOneOfPerm([PERMISSIONS.report.approved])}
              >
                {t('Approve')}
              </Button>
            ) : null
          }
          {
            //? Can print report when: have selected report, that report isn't reading
            canSeeReport(selectedReport) && selectedReport ? (
              <Button
                type="primary"
                onClick={() => {
                  if (layoutsQuery.find((it) => it?.id == reportForm.getFieldValue('layout_id'))) {
                    setOpenPrintDialog(true);
                  } else {
                    message.error(t('No layout selected yet'));
                  }
                }}
                className="ml-2"
                disabled={
                  !keycloak.hasOneOfPerm([PERMISSIONS.report.print]) ||
                  selectedReport?.status == REPORT_STATUS.READING
                }
              >
                {t('Preview')}
              </Button>
            ) : null
          }
        </div>

        {openPrintDialog && (
          <PrintModal
            handleClose={() => setOpenPrintDialog(false)}
            report={selectedReport}
            order={selectedOrder}
            layout={layoutsQuery.find((it) => it?.id == reportForm.getFieldValue('layout_id'))}
            study={study || assign?.order?.study}
          />
        )}
        {openSelectTemplate && (
          <SelectTemplate
            visible={openSelectTemplate}
            onCancel={() => setOpenSelectTemplate(false)}
            onSelect={(template) => {
              if (
                template?.layout_id &&
                displayedLayouts.find((it) => it?.id === template?.layout_id)
              ) {
                reportForm.setFieldValue('layout_id', template?.layout_id || '');
              }
              reportForm.setFieldValue('content', template?.content || '');
              reportForm.setFieldValue('conclusion', template?.conclusion || '');
              setOpenSelectTemplate(false);
            }}
            templates={templates}
            userInfo={userInfo}
            modal={modal}
          />
        )}
        {openMatchOrder ? (
          <MatchOrder
            visible={openMatchOrder}
            study={study}
            onCancel={() => setOpenMatchOrder(false)}
            pendingOrderIds={pendingOrderIds}
            message={message}
          />
        ) : null}
        {openCreateOrder ? (
          <CreateOrderModal
            message={message}
            visible={openCreateOrder}
            institutionId={study?.institution_id}
            onCancel={() => setOpenCreateOrder(false)}
            onFinish={(orderIds) => {
              setPendingOrderIds(orderIds);
              setOpenMatchOrder(true);
              setOpenCreateOrder(false);
            }}
            disablePatientSearch={study?.match_status === MATCH_STATUS.Matched}
            defaultValues={
              study?.match_status === MATCH_STATUS.Matched
                ? {
                    patient_name: study?.patient_name,
                    birth_date: study?.patient_birth_date,
                    sex: study?.patient_sex,
                    patient_id: study?.patient_id,
                    device_id: study?.device_id,
                    visit_id: study?.visit_id,
                    // order_group: get(study?.order_group, '0', ''),
                    refer_doctor_name: get(study?.refer_doctor_name, '0', ''),
                    order_doctor_name: get(study?.order_doctor_name, '0', ''),
                    diagnostic: get(study?.diagnostic, '0', ''),
                  }
                : {
                    patient_name: study?.patient_name,
                    birth_date: study?.patient_birth_date,
                    sex: study?.patient_sex,
                    patient_id: study?.patient_id,
                    device_id: study?.device_id,
                  }
            }
          />
        ) : null}
      </ConfigProvider>
    </div>
  );
};

export default memo(
  ReportForm,
  (prev, next) =>
    isEqual(prev.study_id, next.study_id) &&
    isEqual(prev.assign, next.assign) &&
    isEqual(prev.attachmentParams, next.attachmentParams) &&
    isEqual(prev.recordVideo, next.recordVideo)
);

export interface ReportFormStates {
  SelectedOrderType:
    | ReturnType<typeof useGetOrders>['data']['data'][number]
    | Parameters<typeof Report>[0]['assign']['order'][][number];
  selectedReport: ReturnType<typeof useGetReports>['data']['data'][number];
  matchedOrders:
    | ReturnType<typeof useGetOrders>['data']['data']
    | Parameters<typeof Report>[0]['assign']['order'][];
  layouts: ReturnType<typeof useGetLayouts>['data']['data'];
}

export type RecordVideo = {
  open: boolean;
  recording: boolean;
};

export type StateReportParams = {
  recordVideo: RecordVideo;
  setRecordVideo: Updater<RecordVideo>;
  warningCloseCamera: (
    callback: any,
    settings?: {
      title?: any;
      content?: any;
      okText?: any;
      cancelText?: any;
    }
  ) => void;
};

export type ReportFormProps = Omit<Parameters<typeof Report>[0], 'visible'> & StateReportParams;
